import { getTicks, TickValue } from '../../../../../core/utils/get-ticks.js';
import type { AnyScale } from '../../../types/scales.js';
import type { GridLines } from '../GridLine.js';

/**
 * Computes the line segments for a grid axis by mapping scale ticks to pixel positions.
 *
 * @param scale - The d3 scale for this axis.
 * @param numTicks - Desired tick count hint passed to the scale.
 * @param offset - Additional pixel offset applied to all lines (e.g. band centering from the parent).
 * @param extent - The perpendicular dimension: height for columns (vertical lines), width for rows (horizontal lines).
 * @param axis - Which axis these lines run along. `'x'` produces vertical column lines; `'y'` produces horizontal row lines.
 */

type ScaleWithTickValue = (tick: TickValue) => number | undefined;

export function computeGridLines(
  scale: AnyScale,
  numTicks: number | undefined,
  offset: number | undefined,
  extent: number,
  axis: 'x' | 'y',
): GridLines {
  const bandwidth =
    'bandwidth' in scale ? (scale as { bandwidth(): number }).bandwidth() : 0;
  const scaleOffset = (offset ?? 0) + bandwidth / 2;
  const resolvedTicks = getTicks(scale, numTicks);

  return resolvedTicks.map((tick, index) => {
    // Math.max(0, ...) prevents negative pixel positions — consistent with the manual ticks path
    const pos = Math.max(
      0,
      ((scale as ScaleWithTickValue)(tick) ?? 0) + scaleOffset,
    );
    return axis === 'x'
      ? { from: { x: pos, y: 0 }, to: { x: pos, y: extent }, index }
      : { from: { x: 0, y: pos }, to: { x: extent, y: pos }, index };
  });
}
